import { Inject, Injectable } from '@angular/core';
import { registerLocaleData  } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import {
  Locale,
  LocaleDetails
} from './locale.domain';
import { LocaleLoaderService } from './locale-loader.service';
import type {
  LocaleLanguage,
  CalendarStrings } from './locale.domain';

@Injectable({
  providedIn: 'root'
})
export class LocaleService {
  readonly onLocaleChange: BehaviorSubject<Locale>;

  private locale: Locale = Locale.en_CA;
  private language: LocaleLanguage;
  private localeDetails: LocaleDetails;

  constructor(@Inject(LocaleLoaderService) private localeLoaderService: LocaleLoaderService) {
    this.onLocaleChange = new BehaviorSubject<Locale>(this.locale);
  }

  /**
   * Converts java locale e.g. en_CA to angular locale en-ca
   *
   * @param javaLocale
   * @returns
   */
  static convertLocale(javaLocale: string): string {
    return javaLocale.replace('_', '-').toLocaleLowerCase();
  }

  /**
   * Loads translations and sets application's locale
   *
   * @param localeString en_CA, en-ca, fr_CA, fr-ca
   * @returns promise resolving when translations are loaded
   */
  async setLocale(localeString: string): Promise<void> {
    const locale = LocaleService.convertLocale(localeString) as Locale;
    const language = (locale as string).split('-')[0] as LocaleLanguage;

    const localeWithTranslation = await this.localeLoaderService.load(locale);
    this.locale = locale;
    this.language = language;
    registerLocaleData(localeWithTranslation.localeData, locale);
    this.initLocaleDetails(locale, localeWithTranslation.localeData);
    this.onLocaleChange.next(this.locale);
  }

  getLocaleDetails(): LocaleDetails {
    return this.localeDetails;
  }

  getLocale(): Locale {
    return this.locale;
  }

  getLanguage(): LocaleLanguage {
    return this.language;
  }

  private initLocaleDetails(locale: Locale, localeData: Array<string>): void {
    const groupSeparator: string = localeData[13][1];
    const decimalSeparator: string = localeData[13][0];
    const currencySymbol: string = localeData[16];

    this.localeDetails = new LocaleDetails(
      locale,
      groupSeparator,
      decimalSeparator,
      currencySymbol,
      this.getCalendarStrings(localeData)
    );
  }

  private getCalendarStrings(localeData: Array<unknown>): CalendarStrings {
    return {
      months: [...localeData[5][2]],
      weekdays: [...localeData[3][2]],
      shortMonths: [...localeData[5][1]]
    } as CalendarStrings;
  }
}
